import { useEffect, useState } from "react";
import { Flag, MoreHorizontal, StickyNote } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { useAuth, useHasPermission } from "../../context/AuthContext";
import { useDemoMode } from "../../lib/demo-mode";
import { cn } from "../../lib/utils";
import { editFlag, flagCall, unflagCall } from "./supervision-api";
import type { ReviewFlag } from "./types";

function shortId(value: string): string {
  return value.length > 12 ? `${value.slice(0, 8)}…` : value;
}

export function FlagControls({callId, flag, onChanged}: {
  callId: string; flag: ReviewFlag | null; onChanged: () => void;
}) {
  const authCtx = useAuth();
  const user = authCtx?.user ?? null;
  const isDemo = useDemoMode();
  const hasTriage = useHasPermission("supervision.triage");
  const canTriage = hasTriage || isDemo;
  const [note, setNote] = useState(flag?.note ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);

  useEffect(() => {
    setNote(flag?.note ?? "");
    setNoteOpen(false);
  }, [flag?.note, flag?.flagged_at, flag?.flagged_by, callId]);

  const act = async (operation: () => Promise<unknown>, close = true) => {
    setBusy(true);
    setError(null);
    try {
      await operation();
      onChanged();
      window.dispatchEvent(new Event("supervision-flags-changed"));
      if (close) setNoteOpen(false);
    } catch (err) {
      setError((err as Error).message || "Could not update the review flag");
    } finally {
      setBusy(false);
    }
  };

  const isMyFlag = Boolean(
    flag && canTriage && (isDemo || !user?.id || flag.flagged_by === user?.id || flag.flagged_by === "supervisor" || flag.flagged_by === "supervisor_demo")
  );
  const owner = flag
    ? flag.flagged_by_name || (flag.flagged_by === user?.id ? "you" : shortId(flag.flagged_by))
    : "";
  const flagTitle = flag
    ? `Flagged by ${owner} · ${new Date(flag.flagged_at).toLocaleString()}`
    : "Flag this call for review";

  if (!canTriage) {
    return flag ? (
      <span title={flagTitle} className="flex h-8 items-center gap-1 rounded-lg border border-warning/40 bg-warning-soft px-2 text-[11px] font-medium text-warning">
        <Flag size={14} className="fill-current" /> Flagged
      </span>
    ) : null;
  }

  return (
    <div className="flex items-center gap-1" data-testid="supervision-flag-controls">
      <button
        type="button"
        disabled={busy}
        onClick={() => void act(() => flag ? unflagCall(callId) : flagCall(callId), false)}
        aria-label={flag ? "Unflag call" : "Flag call for review"}
        aria-pressed={Boolean(flag)}
        title={flagTitle}
        className={cn(
          "relative flex h-8 items-center gap-1 rounded-lg border px-2 text-[11px] font-medium transition disabled:cursor-not-allowed disabled:opacity-50",
          flag
            ? "border-warning/40 bg-warning-soft text-warning"
            : "border-line bg-soft text-ink-3 hover:text-ink",
        )}
      >
        <Flag size={14} className={flag ? "fill-current" : undefined} />
        {flag ? <span>Flagged</span> : <span>Flag</span>}
      </button>

      {flag && isMyFlag ? (
        <Popover.Root
          open={noteOpen}
          onOpenChange={(open: boolean) => {
            setNoteOpen(open);
            if (!open) setNote(flag.note ?? "");
          }}
        >
          <Popover.Trigger asChild>
            <button
              type="button"
              aria-label={flag.note ? "Edit flag note" : "Add flag note"}
              title={flag.note ? "Edit review note" : "Add review note"}
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-lg border transition",
                flag.note
                  ? "border-brand/30 bg-brand-soft text-brand"
                  : "border-line bg-soft text-ink-3 hover:text-ink",
              )}
            >
              <StickyNote size={14} className={flag.note ? "fill-current" : undefined} />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
          <Popover.Content
            align="end"
            sideOffset={8}
            className="z-50 w-[min(340px,calc(100vw-24px))] rounded-xl border border-line bg-elevated p-3 text-ink shadow-xl outline-none"
          >
            <div className="mb-2">
              <p className="text-xs font-semibold text-ink">Review note</p>
              <p className="mt-0.5 text-[10px] text-ink-3">Flagged by {owner} · {new Date(flag.flagged_at).toLocaleString()}</p>
            </div>
            <textarea
              aria-label="Review note"
              className="w-full resize-none rounded-lg border border-line bg-canvas p-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
              value={note}
              maxLength={1000}
              placeholder="Optional note for later review"
              onChange={(event) => setNote(event.target.value)}
              rows={4}
            />
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => void act(() => unflagCall(callId))}
                className="mr-auto text-xs text-danger transition disabled:opacity-50"
              >
                Remove flag
              </button>
              <button
                type="button"
                onClick={() => { setNote(flag.note ?? ""); setNoteOpen(false); }}
                className="rounded-lg px-2.5 py-1.5 text-xs text-ink-3 transition hover:bg-soft hover:text-ink"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy || note === flag.note}
                className="rounded-lg bg-brand px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-hover disabled:opacity-50"
                onClick={() => void act(() => editFlag(callId, note))}
              >
                Save
              </button>
            </div>
          </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      ) : null}

      {flag && canTriage ? (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label="More call actions"
              title="More call actions"
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-soft text-ink-3 transition hover:text-ink"
            >
              <MoreHorizontal size={15} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
          <DropdownMenu.Content align="end" sideOffset={6} className="z-50 min-w-44 rounded-lg border border-line bg-elevated p-1 text-ink shadow-xl outline-none">
            <DropdownMenu.Item
              disabled={busy}
              onSelect={() => void act(() => unflagCall(callId))}
              className="flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-xs text-danger outline-none focus:bg-danger-soft"
            >
              <Flag /> Remove review flag
            </DropdownMenu.Item>
          </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      ) : null}

      {error ? (
        <span role="alert" className="absolute right-4 top-[72px] z-50 max-w-[300px] rounded-lg border border-danger/30 bg-elevated px-2 py-1 text-[11px] text-danger shadow-lg">
          {error}
        </span>
      ) : null}
    </div>
  );
}
